/**
 * TOSS Web Application — Client portal: the notifications bell
 *
 * Bell plus unread badge, at every width since release Р2 — including the phone, where the
 * bottom bar no longer carries «Уведомления» (`showInBottomNav: false` in
 * `clientNavigationConfig.ts`) and this is the only unread count in the chrome.
 *
 * Two behaviours, one per breakpoint, and both are deliberate:
 *  - phone — a LINK to the full-screen feed. A panel hanging off a bell on a 375 px screen
 *    covers the screen anyway, at which point it is a screen; the real one has a back arrow,
 *    filters and the history behind it.
 *  - wide — a popover under the bell, fetched only once it has been opened, so an unopened
 *    panel costs nothing on every page of the portal.
 *
 * Lived inside `ClientHeader` until «Заявки» and «Документы» grew their own
 * `ClientSectionAppBar`: two bars needing the same bell is what makes it a file rather than
 * a local function.
 */

import { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { TopBarAction } from '@/components/topbar';
import { ROUTES } from '@/constants';
import { useResponsive } from '@/hooks/useResponsive';
import { useClientNotificationsUnread } from '@/hooks/api/clientPortal/useClientBadges';
import {
  flattenClientNotifications,
  useClientNotifications,
  useMarkAllClientNotificationsRead,
  useMarkClientNotificationRead,
} from '@/hooks/api/clientPortal/useClientNotifications';
import { useToast } from '@/store/uiStore';
import { ClientNotificationsPopover } from './ClientNotificationsPopover';

// ============================================================================
// Component
// ============================================================================

export function ClientNotificationsBell(): JSX.Element {
  const location = useLocation();
  const { showMobileNav } = useResponsive();
  const { data } = useClientNotificationsUnread();
  const count = data?.count ?? 0;
  const isActive = location.pathname === ROUTES.client.notifications;

  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // The feed itself is only fetched on a wide screen, and only once the panel has been
  // opened: on a phone the bell is a link and never needs a list, and an unopened panel must
  // not cost a request on every page of the portal.
  const feed = useClientNotifications({ enabled: !showMobileNav && isOpen });
  const markRead = useMarkClientNotificationRead();
  const markAll = useMarkAllClientNotificationsRead();

  const label = count > 0 ? `Уведомления, непрочитанных: ${count}` : 'Уведомления';

  // The bar's icon control, shared with the internal application since 2026-08-26: the sizes
  // this file used to spell out itself (40/24 on a phone, 36/20 above it) are what that layer
  // now gives every icon on either surface. What stays here is the pair of BEHAVIOURS, which is
  // the reason this is a component and not a call site: a link on a phone, a popover above it.
  if (showMobileNav) {
    return (
      <TopBarAction
        icon={Bell}
        label={label}
        badge={count}
        to={ROUTES.client.notifications}
        current={isActive}
        active={isActive}
      />
    );
  }

  return (
    <>
      <TopBarAction
        ref={triggerRef}
        icon={Bell}
        label={label}
        badge={count}
        active={isActive || isOpen}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
      />

      <ClientNotificationsPopover
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        triggerRef={triggerRef}
        notifications={flattenClientNotifications(feed.data)}
        isLoading={feed.isLoading}
        unreadCount={count}
        onMarkAll={() => markAll.mutate()}
        isMarkingAll={markAll.isPending}
        onMarkRead={(id) => markRead.mutate(id)}
      />
    </>
  );
}

export default ClientNotificationsBell;
